/**
 * Design tokens — the visual design-system primitives the MUI theme cannot
 * express per-value (color helpers, the mono font stack, elevations).
 *
 * THEME IS INTENTIONALLY NEUTRAL: these values are carried over verbatim from
 * the dink-admin reference design system and are NOT a wsr-admin brand tier. A
 * dedicated re-theme spec can replace the palette/typography later
 * (WSRADM-0004 delivery, Operator-confirmed). Do not invent wsr-admin brand
 * values here in the shell unit.
 *
 * dink-admin's membership-business token maps (member STATUS chips, membership
 * TIER chips, member-avatar tint table) are deliberately NOT ported — they are
 * dink domain data, not design-system primitives, and the wsr-admin shell has no
 * consumer for them.
 */

// ---- Neutral brand palette (carried over; pending re-theme) ----
export const COURT_GREEN = '#2E7D52';
export const COURT_GREEN_DARK = '#266B46';
export const PAGE_BG = '#F4F2EE';

// Reusable green tints (active nav bg, alert surfaces, etc.).
export const GREEN_TINTS = {
  navActive: '#E8F0EA',
  chip: '#E5EFE9',
  surfaceA: '#F3F8F4',
  surfaceB: '#F0F5F1',
} as const;

/**
 * Monospace stack for numbers, IDs and keys. `Roboto Mono` is self-hosted via
 * `@fontsource/roboto-mono` (imported in `fonts.ts`).
 */
export const MONO = "'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

/** UI/body font stack — `Roboto`, self-hosted via `@fontsource/roboto`. */
export const SANS = "'Roboto', system-ui, Arial, sans-serif";

/**
 * Material Symbols Outlined font-family. Provided by
 * `@fontsource-variable/material-symbols-outlined`, whose `@font-face` declares
 * this exact family name. Referenced by the `<Icon>` glyph wrapper.
 */
export const MATERIAL_SYMBOLS_FONT = "'Material Symbols Outlined Variable'";

// ---- Shadows (card / dialog / snackbar elevations) ----
export const SHADOWS = {
  card: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
  dialog: '0 11px 15px -7px rgba(0,0,0,0.2), 0 24px 38px 3px rgba(0,0,0,0.14)',
  snackbar: '0 6px 16px rgba(0,0,0,0.3)',
} as const;

// ---- Shared color helpers ----

/** Build the `sx` for a tonal chip (Chip can't theme per-value colors). */
export const chipSx = (c: { fg: string; bg: string }) => ({
  bgcolor: c.bg,
  color: c.fg,
  fontWeight: 500,
});

/** Two-letter initials from a display name (for avatar circles). */
export function initialsOf(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
